import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import ExcelJS from 'exceljs';
import {
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  OpenInNew as OpenInNewIcon,
  Assignment as AssignmentIcon,
  Download as DownloadIcon,
  ReceiptLong as FacturacionIcon,
} from '@mui/icons-material';
import { type GridColDef, type GridRenderCellParams } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, GridEditButton, MainSectionPanel } from '../../../components/common';
import { PermissionGuard } from '../../../auth/components/PermissionGuard';
import { useParteProduccionStore } from './store/useParteProduccionStore';
import { ParteProduccionService } from './services/ParteProduccionService';
import type { ParteResumen } from './types';

type EstadoParte = 'PEN' | 'APR' | 'ANU';

const estadoLabels: Record<EstadoParte, string> = {
  'PEN': 'Pendiente',
  'APR': 'Aprobado',
  'ANU': 'Anulado',
};

const estadoColors: Record<EstadoParte, 'warning' | 'success' | 'error'> = {
  'PEN': 'warning',
  'APR': 'success',
  'ANU': 'error',
};

const ParteProduccionPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [resumenData, setResumenData] = useState<ParteResumen[]>([]);
  const [resumenLoading, setResumenLoading] = useState(false);
  const {
    partes,
    loading,
    error,
    fetchPartes,
    clearError
  } = useParteProduccionStore();

  // Filter partes by tab
  const filteredPartes = partes.filter((parte) => {
    switch (selectedTab) {
      case 0: // Pendientes
        return parte.estado === 'PEN';
      case 1: // Aprobadas
        return parte.estado === 'APR';
      case 2: // Anuladas
        return parte.estado === 'ANU';
      case 3: // Lista (todos)
        return true;
      default:
        return true;
    }
  });

  // Resumen columns for Lista tab
  const resumenColumns: GridColDef[] = [
    { field: 'numeroComprobante', headerName: '#', width: 60, type: 'number' },
    { field: 'fechaRecepcion', headerName: 'Fecha Recepción', width: 120,
      valueFormatter: (value: string) => value ? new Date(value).toLocaleDateString('es-PE') : '' },
    { field: 'ospaProveedora', headerName: 'OSPA Proveedora', width: 200, flex: 1 },
    { field: 'numeroDerc', headerName: 'N° DERC', width: 130 },
    { field: 'numeroMallas', headerName: 'Mallas', width: 70, type: 'number' },
    { field: 'numeroManojos', headerName: 'Manojos', width: 80, type: 'number' },
    { field: 'mediaValvaKg', headerName: 'Media Valva (kg)', width: 130, type: 'number' },
    { field: 'talloCoralKg', headerName: 'Tallo Coral (kg)', width: 130, type: 'number' },
    { field: 'talloSoloKg', headerName: 'Tallo Solo (kg)', width: 120, type: 'number' },
    { field: 'destino', headerName: 'Destino', width: 150 },
  ];

  const loadResumen = useCallback(async () => {
    setResumenLoading(true);
    try {
      const data = await ParteProduccionService.getPartesResumen();
      setResumenData(data);
    } catch {
      // Error handled by interceptor
    } finally {
      setResumenLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedTab === 3 && resumenData.length === 0) {
      loadResumen();
    }
  }, [selectedTab, resumenData.length, loadResumen]);

  const handleExportExcel = async () => {
    if (resumenData.length === 0) return;

    const wb = new ExcelJS.Workbook();
    wb.creator = 'CPP Sistema';
    wb.created = new Date();
    const ws = wb.addWorksheet('Resumen de Partes');

    const COL_COUNT = 13;

    // --- Title ---
    ws.mergeCells('A1:M1');
    const titleCell = ws.getCell('A1');
    titleCell.value = 'Reporte de Partes de Producción';
    titleCell.font = { name: 'Calibri', size: 16, bold: true, color: { argb: 'FF1A237E' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    ws.getRow(1).height = 32;

    // --- Date ---
    ws.mergeCells('A2:M2');
    const dateCell = ws.getCell('A2');
    dateCell.value = `Generado: ${new Date().toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`;
    dateCell.font = { name: 'Calibri', size: 10, italic: true, color: { argb: 'FF757575' } };
    dateCell.alignment = { horizontal: 'center' };
    ws.getRow(2).height = 20;

    // --- Empty row ---
    ws.getRow(3).height = 8;

    // --- Headers ---
    const headers = ['#', 'Fecha Recepción', 'OSPA Proveedora', 'Zona de Extracción', 'Punto de Desembarque', 'Certificado de Procedencia', 'N° DERC', 'Mallas', 'Manojos', 'Media Valva (kg)', 'Tallo Coral (kg)', 'Tallo Solo (kg)', 'Destino'];
    const headerRow = ws.getRow(4);
    headers.forEach((h, i) => {
      const cell = headerRow.getCell(i + 1);
      cell.value = h;
      cell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF0D47A1' } },
        bottom: { style: 'thin', color: { argb: 'FF0D47A1' } },
        left: { style: 'thin', color: { argb: 'FF0D47A1' } },
        right: { style: 'thin', color: { argb: 'FF0D47A1' } },
      };
    });
    headerRow.height = 28;

    // --- Data rows ---
    const thinBorder: Partial<ExcelJS.Borders> = {
      top: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      bottom: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      left: { style: 'thin', color: { argb: 'FFE0E0E0' } },
      right: { style: 'thin', color: { argb: 'FFE0E0E0' } },
    };

    let totalMallas = 0, totalManojos = 0, totalMV = 0, totalTC = 0, totalTS = 0;

    resumenData.forEach((r, idx) => {
      const row = ws.getRow(5 + idx);
      const values = [
        r.numeroComprobante,
        r.fechaRecepcion ? new Date(r.fechaRecepcion).toLocaleDateString('es-PE') : '',
        r.ospaProveedora || '',
        r.zonaExtraccion || '',
        r.puntoDesembarque || '',
        '',  // Certificado de Procedencia (vacío)
        r.numeroDerc || '',
        r.numeroMallas,
        r.numeroManojos,
        r.mediaValvaKg,
        r.talloCoralKg,
        r.talloSoloKg,
        r.destino || '',
      ];
      values.forEach((v, i) => {
        const cell = row.getCell(i + 1);
        cell.value = v;
        cell.font = { name: 'Calibri', size: 10 };
        cell.border = thinBorder;
        cell.alignment = { vertical: 'middle' };
      });
      // Zebra striping
      if (idx % 2 === 1) {
        for (let i = 1; i <= COL_COUNT; i++) {
          row.getCell(i).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
        }
      }
      // Number formatting for kg columns (10=MV, 11=TC, 12=TS)
      row.getCell(10).numFmt = '#,##0.00';
      row.getCell(11).numFmt = '#,##0.00';
      row.getCell(12).numFmt = '#,##0.00';
      // Center align numeric columns
      [1, 8, 9, 10, 11, 12].forEach(c => { row.getCell(c).alignment = { horizontal: 'center', vertical: 'middle' }; });

      totalMallas += r.numeroMallas || 0;
      totalManojos += r.numeroManojos || 0;
      totalMV += r.mediaValvaKg || 0;
      totalTC += r.talloCoralKg || 0;
      totalTS += r.talloSoloKg || 0;
    });

    // --- Totals row ---
    const totalsRowNum = 5 + resumenData.length;
    const totalsRow = ws.getRow(totalsRowNum);
    ws.mergeCells(`A${totalsRowNum}:G${totalsRowNum}`);
    const totalLabelCell = totalsRow.getCell(1);
    totalLabelCell.value = 'TOTALES';
    totalLabelCell.font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FF1A237E' } };
    totalLabelCell.alignment = { horizontal: 'right', vertical: 'middle' };

    const totalValues: (number | null)[] = [null, null, null, null, null, null, null, totalMallas, totalManojos, totalMV, totalTC, totalTS, null];
    totalValues.forEach((v, i) => {
      const cell = totalsRow.getCell(i + 1);
      if (v != null) cell.value = v;
      cell.font = { name: 'Calibri', size: 11, bold: true };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } };
      cell.border = {
        top: { style: 'medium', color: { argb: 'FF1565C0' } },
        bottom: { style: 'medium', color: { argb: 'FF1565C0' } },
        left: { style: 'thin', color: { argb: 'FF90CAF9' } },
        right: { style: 'thin', color: { argb: 'FF90CAF9' } },
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });
    totalsRow.getCell(10).numFmt = '#,##0.00';
    totalsRow.getCell(11).numFmt = '#,##0.00';
    totalsRow.getCell(12).numFmt = '#,##0.00';
    totalsRow.height = 26;

    // --- Column widths ---
    ws.columns = [
      { width: 6 },   // #
      { width: 16 },  // Fecha
      { width: 28 },  // OSPA
      { width: 20 },  // Zona Extracción
      { width: 22 },  // Punto Desembarque
      { width: 22 },  // Cert. Procedencia
      { width: 16 },  // DERC
      { width: 10 },  // Mallas
      { width: 10 },  // Manojos
      { width: 16 },  // Media Valva
      { width: 16 },  // Tallo Coral
      { width: 14 },  // Tallo Solo
      { width: 18 },  // Destino
    ];

    // --- Download ---
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `partes_resumen_${new Date().toISOString().slice(0, 10)}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleNavigateToGuiaProduccion = (idGuiaProduccion: number) => {
    navigate(`/produccion/guias-produccion/${idGuiaProduccion}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'numeroComprobante',
      headerName: '#',
      width: 80,
      type: 'number'
    },
    {
      field: 'numeroGuiaProduccion',
      headerName: 'Guía Prod',
      width: 95,
      renderCell: (params: GridRenderCellParams) => {
        const idGuiaProduccion = params.row.idGuiaProduccion;
        const numeroGuia = params.value;

        if (!idGuiaProduccion) return '-';

        return (
          <Tooltip title="Ver Guía de Producción">
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                cursor: 'pointer',
                color: 'primary.main',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleNavigateToGuiaProduccion(idGuiaProduccion);
              }}
            >
              {numeroGuia || idGuiaProduccion}
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'numeroDerc',
      headerName: 'N° DERC',
      width: 130,
    },
    {
      field: 'fecha',
      headerName: 'Fecha',
      width: 90,
      type: 'date',
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleDateString('es-ES');
      }
    },
    {
      field: 'asociacion',
      headerName: 'Asociación',
      width: 120
    },
    {
      field: 'cliente',
      headerName: 'Cliente',
      width: 180,
      flex: 1,
      minWidth: 150
    },
    {
      field: 'responsable',
      headerName: 'Responsable',
      width: 110
    },
    {
      field: 'numeroGuiaRemision',
      headerName: 'G.R. Remitente',
      width: 120
    },
    {
      field: 'numeroGuiaRemisionTransportista',
      headerName: 'G.R. Transportista',
      width: 130
    },
    {
      field: 'numeroMallas',
      headerName: 'Mallas',
      width: 75,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        if (params.value == null) return '';
        return (
          <Chip
            label={params.value}
            size="small"
            color="info"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'cajas',
      headerName: 'Cajas',
      width: 70,
      type: 'number',
      renderCell: (params: GridRenderCellParams) => {
        if (params.value == null) return '';
        return (
          <Chip
            label={params.value}
            size="small"
            color="primary"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 110,
      renderCell: (params: GridRenderCellParams) => {
        const estado = (params.value as EstadoParte) || 'PEN';
        return (
          <Chip
            label={estadoLabels[estado] || estado}
            color={estadoColors[estado] || 'default'}
            size="small"
          />
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isPendiente = params.row.estado === 'PEN';
        const isAprobada = params.row.estado === 'APR';
        const isAnulada = params.row.estado === 'ANU';

        // Anuladas: sin acciones
        if (isAnulada) return null;

        return (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {isPendiente ? (
              <PermissionGuard permission="PRODUCCION_EDITAR">
              <GridEditButton
                onClick={() => handleEdit(params.row.id)}
                tooltip="Editar"
                fontSize="small"
                ariaLabel={`Editar parte ${params.row.numeroComprobante}`}
              />
              </PermissionGuard>
            ) : isAprobada ? (
              <>
                <Tooltip title="Ver detalle">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => handleView(params.row.id)}
                    aria-label={`Ver parte ${params.row.numeroComprobante}`}
                  >
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <PermissionGuard permission="FACTURACION_GENERAR">
                <Tooltip title="Generar Orden de Facturación">
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/administracion/orden-facturacion/nueva?parteId=${params.row.id}`)}
                    sx={{ color: '#0f2847', '&:hover': { bgcolor: '#f0f7ff' } }}
                    aria-label={`Generar facturación parte ${params.row.numeroComprobante}`}
                  >
                    <FacturacionIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                </PermissionGuard>
              </>
            ) : null}
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    fetchPartes();
  }, [fetchPartes]);

  const _handleAdd = () => {
    navigate('/produccion/partes-produccion/new');
  };
  void _handleAdd; // Explicitly mark as intentionally unused

  const handleEdit = (id: number) => {
    navigate(`/produccion/partes-produccion/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/produccion/partes-produccion/${id}?readonly=true`);
  };

  const handleCloseError = () => {
    clearError();
  };

  const handleGoToGuiasProduccion = () => {
    navigate('/produccion/guias-produccion');
  };

  return (
    <MainSectionPanel>
      <PageTitle
        title="Partes de Producción"
        subtitle="Gestión de Partes de Producción"
        flowStep={3}
        actions={
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AssignmentIcon />}
            onClick={handleGoToGuiasProduccion}
            size="small"
          >
            Guías Producción
          </Button>
        }
      />

      <ListLayoutPanel>

        {error && (
          <Alert
            severity="error"
            onClose={handleCloseError}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={selectedTab}
            onChange={(_e, newValue) => setSelectedTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={`Pendientes (${partes.filter(p => p.estado === 'PEN').length})`} />
            <Tab label={`Aprobadas (${partes.filter(p => p.estado === 'APR').length})`} />
            <Tab label={`Anuladas (${partes.filter(p => p.estado === 'ANU').length})`} />
            <Tab label={`Lista (${partes.length})`} />
          </Tabs>
        </Box>

        {selectedTab === 3 ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExportExcel}
                disabled={resumenData.length === 0}
              >
                Exportar Excel
              </Button>
            </Box>
            {resumenLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <DataGrid
                rows={resumenData}
                columns={resumenColumns}
                loading={resumenLoading}
                getRowId={(row) => row.numeroComprobante}
                pageSize={50}
              />
            )}
          </>
        ) : loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={filteredPartes}
            columns={columns}
            loading={loading}
            getRowId={(row) => row.id}
            pageSize={25}
          />
        )}
      </ListLayoutPanel>
    </MainSectionPanel>
  );
};

export default ParteProduccionPage;